/**
 * BLACKOUT — complexo urbano árido: alojamento ao norte, galpão metálico a
 * leste, fileira de comércio ao sul, oficina a oeste e um pátio murado no meio.
 *
 * Este é o mapa que está no ar. Ele nasceu como 1900 linhas dentro do Level e
 * foi transposto para o contrato de mapa SEM mudar um milímetro: a ordem das
 * chamadas é a ordem original porque o rng é um fluxo único — trocar duas
 * linhas de lugar move todo o entulho do mapa. `tools/dump-level.mjs` compara
 * colisor por colisor contra a versão anterior; é ele que prova isso.
 *
 * Convenção: +X leste, +Z sul, Y para cima. Uma unidade = um metro.
 * O contrato completo está no topo de src/world/MapKit.js.
 */
export default {
  id: 'blackout',
  nome: 'Blackout',
  regiao: 'Complexo árido',
  seed: 20260813,

  bounds: { min: [-34, -1, -34], max: [34, 20, 34] },

  // Frente é -Z: o jogador nasce no portão sul olhando para dentro do complexo.
  player: { spawn: [0, 1.68, 29.5], yaw: 0 },

  // [x, yPé, z, yaw, kind, grounded]
  spawns: [
    [-16.5, 0, -18.0, 0.2, 'enemy', true],
    [-21.0, 0, -14.5, 0.9, 'enemy', true],
    [-12.5, 3.3, -21.0, 1.6, 'enemy', false],
    [-16.0, 6.95, -14.0, 3.1, 'sniper', false],
    [21.0, 0, -10.0, 4.5, 'enemy', true],
    [24.5, 0, -18.0, 3.3, 'enemy', true],
    [28.0, 4.4, -12.0, 3.9, 'sniper', false],
    [17.0, 0, 2.0, 3.6, 'enemy', true],
    [-14.5, 0, 23.5, 0.1, 'enemy', true],
    [-20.0, 0, 24.0, 0.3, 'enemy', true],
    [-13.0, 3.95, 22.0, 0.0, 'sniper', false],
    [-28.0, 0, 1.5, 1.5, 'enemy', true],
    [-2.5, 0, -20.0, 2.9, 'patrol', true],
    [12.0, 0, -22.0, 3.4, 'patrol', true],
    [-9.0, 0, 10.0, 2.6, 'patrol', true],
    [18.0, 0, 16.0, 3.9, 'patrol', true],
    [-24.0, 0, -20.0, 0.7, 'patrol', true],
    [4.0, 0, 24.0, 3.0, 'patrol', true],
  ],

  // [x, y, z, cor, intensidade, alcance, sombra]
  luzes: [
    [-16.5, 2.85, -19.0, 0xffc08a, 26, 15, true],   // térreo do alojamento
    [-13.0, 6.0, -20.5, 0xffb87a, 16, 12, false],   // andar de cima do alojamento
    [21.0, 6.6, -9.0, 0xd8e4f0, 40, 24, true],      // vão principal do galpão
    [22.0, 3.6, 1.5, 0xffce9a, 14, 12, false],      // canto sul do galpão
    [-14.0, 2.9, 23.5, 0xffb066, 20, 13, false],    // interior do comércio
    [7.8, 2.7, 32.0, 0xfff0c0, 12, 10, false],      // guarita do portão
  ],
  // Só as duas mais fortes piscam; o resto fica firme, senão o mapa inteiro
  // parece ter problema elétrico e a piscada deixa de significar alguma coisa.
  piscam: [0, 4],

  ambiente: {
    // 48 graus é meio da manhã: alto o bastante para ler como luz do dia e pôr
    // luz nas superfícies horizontais, baixo o bastante para as paredes ainda
    // jogarem sombra longa e direcional.
    sol: { azimute: 118, elevacao: 48, intensidade: 11.0, cor: 0xfff2da },
    // Paleta casada com esse sol alto. O par zênite/horizonte anterior era de
    // fim de tarde e lia como crepúsculo roxo sobre um pátio iluminado — a
    // pista mais óbvia de que céu e iluminação foram feitos em horas diferentes.
    ceu: { zenite: 0x4a86c8, horizonte: 0xcfd0c4, chao: 0x6d6250, corSol: 0xfff4e0, turbidez: 1.2, neblina: 0.34 },
    // Preenchimento do céu importa mais do que parece: ao ar livre o chão na
    // sombra fica cerca de um oitavo do chão iluminado, não um centésimo.
    hemi: { ceu: 0x8fb2d8, chao: 0x7d6b4e, intensidade: 2.6 },
    // Leve o bastante para dar perspectiva aérea nos 70 m da arena sem lavar o
    // contraste do fundo.
    fog: { cor: 0xc6b899, densidade: 0.0034 },
    paleta: {
      terreno: 'sand', piso: 'asphalt', parede: 'plaster', estrutura: 'concrete',
      metal: 'metalPainted', ferro: 'gunMetal', madeira: 'woodPlank',
      ferrugem: 'rust', pano: 'fabric', interior: 'tile',
    },
  },

  build(kit, ctx) {
    const rng = ctx.rng;
    // --- FASE 1: geometria ---------------------------------------------------
    chao(kit, rng);
    perimetro(kit, rng);
    alojamento(kit, rng);
    galpao(kit);
    comercio(kit);
    oficina(kit);
    patio(kit, rng);
    fiacao(kit);
    // --- FASE 2: espalhamento (precisa dos colisores já fechados) -------------
    espalhamento(kit, rng);
  },
};

// ---------------------------------------------------------------------------
// chão
// ---------------------------------------------------------------------------

function chao(kit, rng) {
  kit.groundPlane('sand', { size: 180, repeat: 45, name: 'ground' });
  kit.groundPlane('asphalt', { size: 48, repeat: 15, x: -2, y: 0.012, z: 2, name: 'pad' });

  // Línguas de areia quebram o retângulo duro onde o asfalto termina. Quads
  // planos dois centímetros salientes — mais barato e mais nítido que blend.
  for (let i = 0; i < 26; i++) {
    const a = rng() * Math.PI * 2;
    const r = 19 + rng() * 7;
    const x = Math.cos(a) * r - 2, z = Math.sin(a) * r + 2;
    const w = 2.5 + rng() * 5, d = 2 + rng() * 4;
    const rot = rng() * Math.PI;
    kit.deco('sand', x, 0.028, z, w, 0.02, d, { rotY: rot, density: 0.35, tint: [1.05, 1.0, 0.92] });
  }
  // pisos internos: ladrilho gasto no alojamento e no comércio
  kit.deco('tile', -16, 0.03, -18, 19, 0.02, 13, { density: 0.9, tint: [0.9, 0.88, 0.85] });
  kit.deco('tile', -14, 0.03, 23, 19.4, 0.02, 9.4, { density: 0.9, tint: [0.88, 0.86, 0.83] });
}

// ---------------------------------------------------------------------------
// muro de perímetro, portão sul e guarita
// ---------------------------------------------------------------------------

function perimetro(kit, rng) {
  const T = 0.7;
  const runs = [
    // [ax, az, bx, bz, brechas[]]  brecha = [u0, u1, alturaRestante]
    [-35, -34.6, 35, -34.6, [[4.5, 10.5, 1.25]]],
    [-35, 34.6, 35, 34.6, []],
    [-34.6, -35, -34.6, 35, [[11.5, 15.5, 1.6]]],
    [34.6, -35, 34.6, 35, []],
  ];
  for (const [ax, az, bx, bz, breaches] of runs) {
    const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
    const s0 = horiz ? ax : az, s1 = horiz ? bx : bz;
    const fixed = horiz ? az : ax;
    // Trechos de 5 m com a crista sorteada — uma caixa longa lê como "um muro",
    // cinco levemente diferentes leem como um muro que alguém levantou.
    const n = Math.round((s1 - s0) / 5);
    for (let i = 0; i < n; i++) {
      const a = s0 + (i / n) * (s1 - s0);
      const b = s0 + ((i + 1) / n) * (s1 - s0);
      let top = 3.75 + rng() * 0.45;
      const br = breaches.find((k) => b > k[0] && a < k[1]);
      if (br) top = br[2] + rng() * 0.25;
      const tint = [0.94 + rng() * 0.12, 0.93 + rng() * 0.1, 0.9 + rng() * 0.1];
      // vão do portão no muro sul
      if (horiz && az > 0 && b > -4.2 && a < 4.2) {
        if (a < -4.2) kit.solid('concrete', (a - 4.2) * 0.5, top * 0.5, fixed, (-4.2 - a), top, T, { tint });
        if (b > 4.2) kit.solid('concrete', (b + 4.2) * 0.5, top * 0.5, fixed, (b - 4.2), top, T, { tint });
        continue;
      }
      kit.solid('concrete', horiz ? (a + b) * 0.5 : fixed, top * 0.5, horiz ? fixed : (a + b) * 0.5,
        horiz ? b - a : T, top, horiz ? T : b - a, { tint, uoff: a * 0.5, voff: a * 0.5 });
      if (!br) {
        // fiada de rincão
        kit.deco('concrete', horiz ? (a + b) * 0.5 : fixed, top + 0.11, horiz ? fixed : (a + b) * 0.5,
          horiz ? b - a : T + 0.22, 0.22, horiz ? T + 0.22 : b - a, { tint: [0.86, 0.85, 0.82], density: 0.9 });
      } else {
        // crista desmoronada: alguns blocos quebrados de pé na borda da brecha
        for (let k = 0; k < 3; k++) {
          const u = kit.lerp(a, b, 0.2 + rng() * 0.6);
          kit.deco('concrete', horiz ? u : fixed, top + 0.16, horiz ? fixed : u,
            horiz ? 0.5 + rng() : T, 0.3 + rng() * 0.4, horiz ? T : 0.5 + rng(),
            { tint: [0.8, 0.78, 0.74], rotY: rng() * 0.3, density: 0.9 });
        }
      }
    }
    // pilastras a cada 10 m, 12 cm salientes para nunca brigarem com a cápsula
    for (let u = s0 + 5; u < s1; u += 10) {
      kit.deco('concrete', horiz ? u : fixed, 2.05, horiz ? fixed : u,
        horiz ? 0.9 : T + 0.24, 4.1, horiz ? T + 0.24 : 0.9, { tint: [0.9, 0.89, 0.86], density: 0.7 });
    }
  }

  // --- portão sul ------------------------------------------------------------
  for (const s of [-1, 1]) {
    kit.solid('concrete', s * 4.7, 2.5, 34.6, 1.1, 5.0, 1.1, { tint: [0.9, 0.89, 0.86], density: 0.7 });
    kit.deco('concrete', s * 4.7, 5.15, 34.6, 1.3, 0.3, 1.3, { tint: [0.85, 0.84, 0.8] });
  }
  kit.solid('concrete', 0, 4.55, 34.6, 8.3, 0.9, 0.8, { tint: [0.9, 0.89, 0.86] });
  kit.deco('metalPainted', 0, 3.85, 34.6, 7.4, 0.5, 0.14, { tint: [0.7, 0.72, 0.7], density: 1.2 });
  // uma folha do portão pendurada, aberta
  kit.deco('metalPainted', -6.0, 1.8, 32.9, 0.12, 3.4, 3.4, { rotY: 0.5, density: 1.1, tint: [0.75, 0.7, 0.62] });
  // guarita ao lado do portão
  kit.wall('concrete', 6.2, 30.6, 6.2, 33.4, 0, 2.9, 0.25, { openings: [{ u0: 31.4, u1: 32.8, y0: 1.0, y1: 2.3 }], trim: 0.14 });
  kit.wall('concrete', 9.4, 30.6, 9.4, 33.4, 0, 2.9, 0.25, {});
  kit.wall('concrete', 6.2, 30.6, 9.4, 30.6, 0, 2.9, 0.25, { openings: [{ u0: 7.0, u1: 8.6, y0: 0, y1: 2.2 }], trim: 0.14 });
  kit.slab('concrete', 5.9, 9.7, 30.3, 33.7, 3.2, 0.28, [], { tint: [0.88, 0.87, 0.84] });
}

// ---------------------------------------------------------------------------
// alojamento norte — dois pavimentos, telhado, sacada, escada interna e externa
// ---------------------------------------------------------------------------

function alojamento(kit, rng) {
  const P = 'plaster';
  const t = 0.5;
  const W = -25.75, E = -6.25, N = -24.75, S = -11.25;
  const F1 = 3.3, F2 = 6.6;
  const tint = [1.0, 0.99, 0.96];

  // fachada sul — é a vista que vale, olhada do pátio
  kit.wall(P, -26, S, -6, S, 0, F2, t, {
    tint,
    trim: 0.22,
    openings: [
      { u0: -24.2, u1: -22.2, y0: 1.05, y1: 2.5 },
      { u0: -17.4, u1: -15.4, y0: 0, y1: 2.45 },
      { u0: -12.4, u1: -10.4, y0: 1.05, y1: 2.5 },
      { u0: -24.6, u1: -23.0, y0: 3.9, y1: 5.5 },
      { u0: -21.6, u1: -19.6, y0: 3.55, y1: 5.75 },
      { u0: -17.6, u1: -15.2, y0: 3.9, y1: 5.5 },
      { u0: -13.0, u1: -10.6, y0: 3.9, y1: 5.5 },
    ],
  });
  // fachada norte
  kit.wall(P, -26, N, -6, N, 0, F2, t, {
    tint,
    trim: 0.22,
    openings: [
      { u0: -19.0, u1: -17.0, y0: 1.6, y1: 2.6 },
      { u0: -22.0, u1: -20.4, y0: 4.4, y1: 5.6 },
      { u0: -14.0, u1: -12.4, y0: 4.4, y1: 5.6 },
    ],
  });
  // empena oeste
  kit.wall(P, W, -25, W, -11, 0, F2, t, {
    tint,
    trim: 0.22,
    openings: [
      { u0: -22.0, u1: -20.0, y0: 1.1, y1: 2.5 },
      { u0: -16.0, u1: -14.0, y0: 1.1, y1: 2.5 },
      { u0: -21.0, u1: -19.0, y0: 3.9, y1: 5.5 },
    ],
  });
  // empena leste — porta térrea para o beco, porta alta na saída da escada
  kit.wall(P, E, -25, E, -11, 0, F2, t, {
    tint,
    trim: 0.22,
    openings: [
      { u0: -14.6, u1: -12.6, y0: 0, y1: 2.45 },
      { u0: -22.0, u1: -20.0, y0: 1.1, y1: 2.5 },
      { u0: -18.4, u1: -16.6, y0: 3.4, y1: 5.6 },
      { u0: -23.0, u1: -21.4, y0: 3.9, y1: 5.5 },
    ],
  });

  // laje do primeiro andar com o vazio da caixa de escada a nordeste
  kit.slab('concrete', -25.5, -6.5, -24.5, -11.5, F1, 0.3, [[-12.0, -8.6, -24.5, -19.0]], { tint: [0.92, 0.9, 0.87] });
  // divisórias do térreo
  kit.wall(P, -16, -24.5, -16, -19.4, 0, F1, 0.24, { openings: [{ u0: -22.6, u1: -21.0, y0: 0, y1: 2.2 }], tint, plinth: false });
  kit.wall(P, -25.5, -17.4, -16, -17.4, 0, F1, 0.24, { openings: [{ u0: -20.4, u1: -18.8, y0: 0, y1: 2.2 }], tint, plinth: false });
  // divisória de cima
  kit.wall(P, -18.0, -24.5, -18.0, -16.0, F1, F2, 0.24, { openings: [{ u0: -21.0, u1: -19.4, y0: F1, y1: F1 + 2.2 }], tint, plinth: false });

  // escada interna térreo -> primeiro, dentro do vazio
  kit.stairs('concrete', -11.4, -19.2, 0, -1, 12, 0.275, 0.44, 1.6, 0, { rail: 'left' });
  // escada interna primeiro -> telhado, dentro da casinha
  kit.stairs('concrete', -24.6, -19.4, 0, -1, 12, 0.275, 0.44, 1.6, F1, { rail: 'right' });

  // laje do telhado com beiral de 0,6 m e o vazio sobre a escada da casinha
  kit.slab('concrete', -26.6, -5.4, -25.6, -10.4, F2 + 0.35, 0.35, [[-25.4, -22.2, -24.4, -19.2]], { tint: [0.9, 0.89, 0.86] });
  // a testeira sob o beiral revela a espessura do telhado visto de baixo
  kit.deco('concrete', -16, F2 + 0.1, -10.45, 21.2, 0.2, 0.3, { tint: [0.82, 0.81, 0.78], density: 0.9 });
  kit.deco('concrete', -16, F2 + 0.1, -25.55, 21.2, 0.2, 0.3, { tint: [0.82, 0.81, 0.78], density: 0.9 });
  // casinha da escada
  kit.wall(P, -25.4, -24.4, -25.4, -19.2, F2 + 0.35, F2 + 2.3, 0.25, { tint, plinth: false });
  kit.wall(P, -22.2, -24.4, -22.2, -19.2, F2 + 0.35, F2 + 2.3, 0.25, { tint, plinth: false });
  kit.wall(P, -25.4, -24.4, -22.2, -24.4, F2 + 0.35, F2 + 2.3, 0.25, { tint, plinth: false });
  kit.slab('metalPainted', -25.7, -21.9, -24.7, -19.0, F2 + 2.5, 0.2, [], { tint: [0.8, 0.82, 0.8] });

  // parapeito com brechas para atirar
  kit.parapet('concrete', -26.4, -5.6, -25.4, -10.6, F2 + 0.35, 0.9, 0.3, [
    ['s', -20.5, -19.0], ['s', -12.0, -10.5], ['e', -22.0, -20.5], ['w', -16.0, -14.5],
  ]);

  // sacada da porta alta do sul
  kit.slab('concrete', -22.9, -18.3, -11.5, -9.4, F1 + 0.05, 0.25, [], { tint: [0.9, 0.89, 0.86] });
  kit.rail(-22.9, -9.4, -18.3, -9.4, F1 + 0.05);
  kit.rail(-22.9, -11.4, -22.9, -9.4, F1 + 0.05);
  kit.rail(-18.3, -11.4, -18.3, -9.4, F1 + 0.05);
  // mãos-francesas da sacada
  for (const x of [-22.4, -20.6, -18.8]) {
    kit.deco('concrete', x, F1 - 0.2, -10.1, 0.22, 0.5, 1.6, { tint: [0.84, 0.83, 0.8], density: 0.9 });
  }

  // escada externa pelo flanco leste até a porta alta
  kit.stairs('concrete', -4.6, -11.6, 0, -1, 12, 0.283, 0.425, 1.8, 0, { rail: 'right' });
  kit.slab('concrete', -6.0, -3.5, -18.6, -16.6, 3.4, 0.25, [], { tint: [0.9, 0.89, 0.86] });
  kit.rail(-3.6, -18.6, -3.6, -16.6, 3.4);
  kit.rail(-6.0, -18.6, -3.6, -18.6, 3.4);

  // tijolo à mostra onde o reboco caiu — remendos tingidos, 2 cm salientes
  const brick = [0.72, 0.44, 0.34];
  const patches = [
    [-23.4, 2.4, S, 2.2, 1.6], [-9.0, 1.0, S, 1.8, 1.4], [-19.2, 4.9, S, 2.6, 1.2],
    [-13.6, 5.2, N, 2.0, 1.5],
  ];
  for (const [x, y, z, w, h] of patches) {
    kit.deco('concrete', x, y, z + (z === S ? 0.26 : -0.26), w, h, 0.05, { tint: brick, density: 2.6, uoff: rng() * 3 });
  }
  // dano de estilhaço acima das janelas do térreo
  kit.deco('concrete', -22.0, 2.75, S + 0.26, 1.1, 0.5, 0.06, { tint: [0.7, 0.68, 0.65], density: 2.2, rotY: 0 });
}

// ---------------------------------------------------------------------------
// galpão leste — chapa nervurada, passarela interna, alçapão de telhado
// ---------------------------------------------------------------------------

function galpao(kit) {
  const M = 'metalPainted';
  const t = 0.4;
  const W = 14.2, E = 29.8, N = -20.8, S = 4.8;
  const H = 8.0;
  const tint = [0.95, 0.98, 1.0];
  const wallOpt = { tint, ribs: 1.15, plinth: true, trim: 0 };

  kit.wall(M, W, -21, W, 5, 0, H, t, {
    ...wallOpt,
    openings: [
      { u0: -10.0, u1: -4.4, y0: 0, y1: 4.6, frame: false },
      { u0: 1.0, u1: 2.4, y0: 0, y1: 2.3 },
      { u0: -18.0, u1: -16.0, y0: 5.6, y1: 7.0, frame: false },
      { u0: -14.0, u1: -12.0, y0: 5.6, y1: 7.0, frame: false },
      { u0: -2.0, u1: 0.0, y0: 5.6, y1: 7.0, frame: false },
    ],
  });
  kit.wall(M, E, -21, E, 5, 0, H, t, {
    ...wallOpt,
    openings: [
      { u0: -17.0, u1: -15.0, y0: 5.6, y1: 7.0, frame: false },
      { u0: -9.0, u1: -7.0, y0: 5.6, y1: 7.0, frame: false },
      { u0: -1.0, u1: 1.0, y0: 5.6, y1: 7.0, frame: false },
    ],
  });
  kit.wall(M, 14, N, 30, N, 0, H, t, {
    ...wallOpt,
    openings: [
      { u0: 20.0, u1: 22.4, y0: 0, y1: 2.9, frame: false },   // rombo de explosão
      { u0: 17.0, u1: 19.0, y0: 5.6, y1: 7.0, frame: false },
      { u0: 25.0, u1: 27.0, y0: 5.6, y1: 7.0, frame: false },
    ],
  });
  kit.wall(M, 14, S, 30, S, 0, H, t, {
    ...wallOpt,
    openings: [
      { u0: 20.0, u1: 23.0, y0: 0, y1: 3.6, frame: false },
      { u0: 16.0, u1: 18.0, y0: 5.6, y1: 7.0, frame: false },
    ],
  });

  // porta de enrolar: guias, tambor e a cortina parada no meio
  kit.deco(M, 14.2, 4.85, -7.2, 0.5, 0.55, 6.0, { tint: [0.8, 0.83, 0.85], density: 1.4 });
  kit.deco(M, 14.05, 4.0, -7.2, 0.14, 1.3, 5.6, { tint: [0.7, 0.73, 0.75], density: 3.0 });
  for (const z of [-10.1, -4.3]) kit.deco(M, 14.2, 2.3, z, 0.6, 4.6, 0.2, { tint: [0.72, 0.75, 0.77], density: 1.6 });
  // chapa de soleira da porta
  kit.deco('rust', 14.2, 0.02, -7.2, 1.2, 0.06, 5.6, { tint: [0.9, 0.85, 0.8], density: 1.2 });

  // telhado com alçapão sobre o topo da escada da passarela
  kit.slab(M, 13.4, 30.6, -21.6, 5.4, H + 0.4, 0.4, [[26.9, 28.9, -8.6, -5.4]], { tint: [0.92, 0.95, 0.97] });
  kit.parapet(M, 13.6, 30.4, -21.4, 5.2, H + 0.4, 0.7, 0.25, [['w', -6, -4], ['s', 22, 24]]);
  // as nervuras do telhado leem a corrugação de longe
  for (let x = 14.4; x < 30.4; x += 1.6) {
    kit.deco(M, x, H + 0.46, -8.1, 0.5, 0.12, 26.6, { tint: [0.88, 0.91, 0.93], density: 1.1 });
  }
  // faixas de telha translúcida acesas por dentro — jeito barato de dizer "há luz ali"
  for (const z of [-15, -8, -1]) {
    kit.deco(M, 22, H - 0.02, z, 6.0, 0.06, 1.4, { tint: [1, 1, 1], density: 0.6 });
  }

  // passarela interna nas paredes leste e norte
  kit.catwalk(M, 26.4, 29.6, -20.4, -2.4, 4.4, { rails: [] });
  kit.catwalk(M, 14.6, 26.4, -20.4, -18.2, 4.4, { rails: [] });
  kit.rail(26.4, -20.4, 26.4, -2.4, 4.4);
  kit.rail(14.6, -18.2, 26.4, -18.2, 4.4);
  kit.rail(26.4, -2.4, 29.6, -2.4, 4.4);
  // escada piso -> passarela
  kit.stairs(M, 27.4, 4.2, 0, -1, 16, 0.275, 0.42, 1.8, 0, { rail: 'left', tint: [0.9, 0.93, 0.95] });
  // escada íngreme passarela -> alçapão
  kit.stairs(M, 27.9, -4.9, 0, -1, 12, 0.334, 0.3, 1.5, 4.4, { rail: 'both', tint: [0.9, 0.93, 0.95] });

  // estantes de armazenamento
  for (const z of [-16.5, -12.5, 0.5]) kit.rack({ x0: 15.4, x1: 18.6, z });

  // escritório de mezanino no canto noroeste, sob a passarela
  kit.wall(M, 14.6, -14.2, 19.4, -14.2, 0, 2.9, 0.2, { openings: [{ u0: 17.6, u1: 19.0, y0: 0, y1: 2.2 }], tint, plinth: false });
  kit.wall(M, 19.4, -18.4, 19.4, -14.2, 0, 2.9, 0.2, { openings: [{ u0: -17.4, u1: -15.6, y0: 1.0, y1: 2.3 }], tint, plinth: false });
  kit.slab(M, 14.4, 19.5, -18.4, -14.1, 3.1, 0.2, [], { tint: [0.85, 0.88, 0.9] });
}

// ---------------------------------------------------------------------------
// comércio sul — beiral profundo, acesso ao telhado, quatro lojas
// ---------------------------------------------------------------------------

function comercio(kit) {
  const P = 'plaster';
  const t = 0.4;
  const N = 18.2, S = 27.8, W = -23.8, E = -4.2;
  const H = 3.6, ROOF = 3.95;
  const tint = [1.02, 0.98, 0.92];

  kit.wall(P, -24, N, -4, N, 0, H, t, {
    tint, trim: 0.2,
    openings: [
      { u0: -22.0, u1: -19.0, y0: 0.95, y1: 2.8 },
      { u0: -17.5, u1: -14.5, y0: 0, y1: 2.4 },
      { u0: -12.5, u1: -9.5, y0: 0.95, y1: 2.8 },
      { u0: -8.0, u1: -5.0, y0: 0, y1: 2.4 },
    ],
  });
  kit.wall(P, -24, S, -4, S, 0, H, t, {
    tint, trim: 0.2,
    openings: [
      { u0: -21.0, u1: -19.6, y0: 1.4, y1: 2.6 },
      { u0: -11.0, u1: -9.6, y0: 1.4, y1: 2.6 },
    ],
  });
  kit.wall(P, W, 18, W, 28, 0, H, t, { tint, trim: 0.2, openings: [{ u0: 24.0, u1: 25.6, y0: 1.2, y1: 2.5 }] });
  kit.wall(P, E, 18, E, 28, 0, H, t, { tint, trim: 0.2, openings: [{ u0: 21.0, u1: 22.6, y0: 1.2, y1: 2.5 }] });
  // paredes-meia entre as lojas, com uma porta de ligação cada
  for (const x of [-18.5, -13.5, -8.7]) {
    kit.wall(P, x, 18.2, x, 27.8, 0, H, 0.22, { tint, plinth: false, openings: [{ u0: 25.4, u1: 26.9, y0: 0, y1: 2.2 }] });
  }

  // telhado: beiral fundo ao norte, para as vitrines ficarem na sombra
  kit.slab('concrete', -24.4, -3.8, 17.0, 28.2, ROOF, 0.35, [], { tint: [0.94, 0.92, 0.88] });
  kit.deco('concrete', -14.1, ROOF - 0.42, 17.05, 20.6, 0.22, 0.3, { tint: [0.8, 0.79, 0.76], density: 0.9 });
  kit.parapet('concrete', -24.2, -4.0, 17.2, 28.0, ROOF, 0.75, 0.28, [
    ['w', 21.0, 23.6], ['n', -20.0, -18.4], ['n', -10.0, -8.4],
  ]);
  // pilares sob a ponta do beiral
  for (let x = -22.5; x <= -5; x += 3.6) {
    kit.solid('woodPlank', x, 1.6, 17.2, 0.16, 3.2, 0.16, { density: 1.6, tint: [0.95, 0.9, 0.82] });
    kit.deco('woodPlank', x, 3.14, 17.6, 0.14, 0.14, 0.9, { density: 1.6, rotY: 0, tint: [0.9, 0.86, 0.8] });
  }

  // escada externa para o telhado, colada na empena oeste
  kit.stairs('concrete', -24.9, 17.6, 0, 1, 13, 0.304, 0.415, 1.8, 0, { rail: 'left' });

  // balcões das duas lojas que atendem pela frente
  for (const x of [-20.5, -11.0]) {
    kit.solid('woodPlank', x, 0.47, 18.9, 2.6, 0.94, 0.7, { density: 1.4, tint: [0.9, 0.85, 0.78] });
    kit.deco('woodPlank', x, 0.96, 18.85, 2.9, 0.06, 0.9, { density: 1.4, tint: [0.98, 0.94, 0.88] });
  }
  // caixas d'água no telhado
  for (const [x, z] of [[-21.0, 20.5], [-7.0, 25.0]]) kit.tank(x, ROOF, z);
}

// ---------------------------------------------------------------------------
// oficina oeste
// ---------------------------------------------------------------------------

function oficina(kit) {
  const C = 'concrete';
  const t = 0.35;
  const W = -32.0, E = -24.2, N = -4.0, S = 6.0;
  const H = 3.1, ROOF = 3.45;
  const tint = [0.98, 0.97, 0.94];

  kit.wall(C, E, -4, E, 6, 0, H, t, { tint, trim: 0.18, openings: [{ u0: -1.2, u1: 3.0, y0: 0, y1: 2.75, frame: false }] });
  kit.wall(C, W, -4, W, 6, 0, H, t, { tint, trim: 0.18, openings: [{ u0: 0.4, u1: 2.0, y0: 1.2, y1: 2.4 }] });
  kit.wall(C, -32, N, -24.2, N, 0, H, t, { tint, trim: 0.18, openings: [{ u0: -30.0, u1: -28.4, y0: 1.2, y1: 2.4 }] });
  kit.wall(C, -32, S, -24.2, S, 0, H, t, { tint, trim: 0.18 });
  kit.slab(C, -32.4, -23.8, -4.4, 6.4, ROOF, 0.3, [], { tint: [0.92, 0.9, 0.87] });
  // Duas brechas no parapeito leste: a de 0,5..2,5 é a posição de tiro sobre o
  // pátio que sempre esteve aqui; a de 3,6..5,6 é a CHEGADA da escada nova
  // (ver abaixo). Sem ela a escada acabaria encostada num muro de 70 cm.
  kit.parapet(C, -32.2, -24.0, -4.2, 6.2, ROOF, 0.7, 0.26, [['e', 0.5, 2.5], ['e', 3.6, 5.6]]);
  // verga sobre o portão e o trilho da porta entortado
  kit.deco('rust', -24.2, 2.95, 0.9, t + 0.3, 0.3, 4.4, { tint: [0.9, 0.85, 0.8], density: 1.4 });
  kit.deco('metalPainted', -24.0, 1.55, -1.0, 0.1, 2.6, 0.5, { rotY: 0.22, density: 1.6, tint: [0.8, 0.8, 0.78] });
  // bancada e painel de ferramentas
  kit.solid('woodPlank', -30.4, 0.45, 4.6, 2.8, 0.9, 0.75, { density: 1.4, tint: [0.9, 0.85, 0.78] });
  kit.deco('metalPainted', -31.7, 1.9, 4.6, 0.1, 1.4, 2.4, { density: 1.6, tint: [0.72, 0.75, 0.75] });
  // borda do fosso de inspeção
  kit.deco(C, -28.0, 0.06, 1.0, 3.4, 0.12, 1.4, { tint: [0.7, 0.69, 0.66], density: 1.2 });

  // Escada externa para o telhado, rente à empena leste.
  //
  // Este telhado — 78 m² de laje com parapeito de 70 cm e uma brecha de tiro
  // aberta para o pátio — era a maior ilha do mapa: `tools/test-rota-alta.mjs`
  // media 106,5 m² inalcançáveis a pé aqui. A brecha no parapeito é a prova de
  // que o acesso estava previsto no desenho e nunca foi construído.
  //
  // Sobe de LESTE para OESTE, perpendicular à empena: 12 x 0,29 = 3,48 m contra
  // 3,45 m de laje, entrando pelo vão de z 3,6..5,6 aberto acima. Quem sobe
  // andando para frente termina em cima do telhado, sem virar.
  //
  // A primeira versão era um lance rente à parede, subindo para o norte em
  // x=-23,3. Ele subia os 3,48 m certinhos — e `test-stairs.mjs` reprovou
  // assim mesmo: o topo fica meio metro a LESTE da borda da laje (x=-23,8),
  // então quem chega lá em cima andando para frente cai do outro lado. Escada
  // que sobe mas não entrega é o mesmo defeito que o Porto tinha, só que com o
  // erro no último passo em vez de no primeiro.
  //
  // O lance fica INTEIRO ao sul do portão da oficina (z -1,2..3,0): encaixado
  // na brecha antiga do parapeito (0,5..2,5) ele cruzaria o portão a 2,3 m de
  // altura e fecharia metade da entrada. Por isso a brecha nova.
  kit.stairs(C, -19.0, 4.6, -1, 0, 12, 0.29, 0.42, 1.6, 0, { rail: 'both' });
}

// ---------------------------------------------------------------------------
// pátio: cobertura dura, contêineres, o casco queimado, alambrado
// ---------------------------------------------------------------------------

function patio(kit, rng) {
  // contêineres — o do alojamento é a subida para a sacada
  kit.container(-19.4, -7.0, 0, 2.85);
  kit.container(9.0, 6.5, Math.PI / 2, 2.6);
  kit.container(24.0, 12.0, 0.0, 2.6);

  // caixotes empilhados levando até o contêiner do alojamento
  kit.crateStack(-14.6, -6.4, 1.05, 2);
  kit.crateStack(-16.4, -5.2, 1.05, 1);

  // cobertura baixa de concreto, deliberadamente na altura do peito e da cintura
  const covers = [
    [-2, 2, 4.2, 1.15, 2.4], [6.4, -6.0, 2.4, 1.45, 2.4], [-7.5, 7.0, 2.6, 1.35, 2.2],
    [11.0, 9.0, 3.2, 1.15, 1.6], [-22.5, 11.5, 6.0, 1.3, 1.3], [24.5, -23.0, 1.4, 1.3, 8.0],
    [2.5, 17.0, 5.0, 1.2, 1.2], [-3.0, -19.0, 1.4, 1.25, 5.0],
  ];
  for (const [x, z, sx, sy, sz] of covers) kit.coverBlock(x, z, sx, sy, sz);

  // floreiras servem de cobertura e quebram o asfalto
  for (const [x, z] of [[-9.5, 15.5], [13.5, 2.0], [-1.0, -12.0]]) kit.planter(x, z);
  // Esta quarta é degrau, não só cobertura: o contêiner de (24, 12) tem 2,6 m e
  // o mantle vence 1,9 — sozinho ele era uma ilha de 12 m² no meio do pátio
  // leste, o único dos três contêineres do mapa sem subida. Com 1,25 m de
  // altura encostada na face oeste dele, a subida fecha em dois lances de 1,25
  // e 1,35 m. `planter` não sorteia nada (ao contrário de `coverBlock` e
  // `crateStack`, que sorteiam o tom e a rotação), então acrescentá-la aqui,
  // no meio da fase 1, não desloca o rng de mais ninguém.
  kit.planter(20.1, 12.0, { h: 1.25 });

  // casco de veículo queimado
  kit.wreck(7.5, 12.5, 0.38);

  // alambrado moldando a aproximação nordeste
  kit.fence(8.0, -22.0, 8.0, -13.0, 2.0);
  kit.fence(8.0, -13.0, 13.6, -13.0, 2.0, [[9.6, 11.6]]);

  // parapeitos de sacaria (as sacas em si vêm instanciadas na fase 2)
  kit.emplacement({ x: 0.5, z: -2.0, yaw: 0.0, len: 4.2, y: 0 });
  kit.emplacement({ x: -12.5, z: 4.0, yaw: Math.PI / 2, len: 3.4, y: 0 });
  kit.emplacement({ x: 17.5, z: 18.5, yaw: 0.35, len: 3.8, y: 0 });
  kit.emplacement({ x: -16.0, z: -13.5, yaw: 0.0, len: 3.2, y: 6.95 });
  kit.emplacement({ x: -12.0, z: 21.0, yaw: Math.PI / 2, len: 3.0, y: 3.95 });

  // chapas onduladas encostadas nas paredes
  for (const [x, z, ry] of [[-8.6, 20.4, 0.1], [16.0, -22.4, 0.0], [-27.4, -9.6, 1.5], [12.4, 5.6, 0.4]]) {
    kit.leanSheet(x, z, ry);
  }

  // barracas de feira soltas no pátio
  for (const [x, z, ry] of [[-6.0, 14.0, 0.2], [1.0, 12.0, -0.15]]) kit.stall(x, z, ry);

  // toldos ao longo da fileira de lojas
  for (let i = 0; i < 4; i++) {
    const x = -21.6 + i * 4.6;
    kit.awning({
      x, z: 18.0, w: 4.2, depth: 2.1, yHigh: 3.28, yLow: 2.62, sag: 0.22,
      tint: i % 2 ? [1.0, 0.66, 0.42] : [0.62, 0.72, 0.82],
    });
  }
}

// ---------------------------------------------------------------------------
// fiação, varais, mastros, antenas
// ---------------------------------------------------------------------------

function fiacao(kit) {
  const cables = [
    [[-6.6, 6.2, -12.0], [13.6, 7.6, -14.0], 1.6],
    [[-6.6, 5.8, -20.0], [8.0, 4.2, -22.0], 1.2],
    [[-24.0, 4.6, 17.4], [-34.2, 4.1, 12.0], 1.1],
    [[-4.2, 4.4, 20.0], [8.6, 3.4, 22.0], 1.4],
    [[13.6, 8.6, 4.0], [8.0, 4.0, 12.0], 1.5],
    [[-24.0, 7.0, -11.0], [-34.2, 4.2, -6.0], 1.9],
    [[-32.2, 3.6, 0.0], [-24.4, 3.4, -1.0], 0.5],
    [[9.4, 3.3, 32.0], [4.7, 4.9, 34.4], 0.4],
  ];
  for (const [a, b, sag] of cables) kit.cable(a, b, sag);

  // varais: o fio mais o pano pendurado, que balança
  kit.laundry(-22.4, 4.35, 21.0, -8.0, 4.35, 21.0, 4);
  kit.laundry(-22.6, 3.6, -10.2, -18.4, 3.6, -10.2, 2);

  // mastros e antenas
  kit.mast(-24.0, 7.05, -13.0, 4.4);
  kit.mast(28.6, 8.6, 2.0, 5.2);
  kit.dish(-8.5, 7.6, -13.5, -0.7);
  kit.dish(16.5, 9.0, -18.0, 2.4);
  kit.dish(-6.5, 4.3, 26.0, 1.8);
  // condensadores no telhado do galpão
  for (const [x, z] of [[18.0, -3.0], [25.0, 1.0]]) kit.acUnit(x, 8.85, z);
  // exaustores de telhado
  for (const [x, z] of [[16.0, -12.0], [24.0, -12.0], [20.0, -6.0]]) kit.vent(x, 8.7, z);
}

// ---------------------------------------------------------------------------
// FASE 2 — espalhamento. Nada aqui pode vir antes da geometria: todo mundo
// consulta groundAt()/isSolid() sobre a lista de colisores já fechada.
// ---------------------------------------------------------------------------

function espalhamento(kit) {
  kit.sandbags();

  kit.crates([
    [-3.4, 9.4], [-2.6, 10.4], [12.6, -9.0], [13.4, -8.2], [17.2, 2.4], [16.4, 3.2],
    [-27.0, 3.0], [-26.2, 2.0], [-19.0, 15.0], [-18.2, 15.9], [5.0, -16.0], [21.0, -15.0],
    [21.9, -15.9], [27.0, 8.0], [-10.0, -8.4], [3.2, 21.0], [-30.0, 12.0], [-29.2, 12.9],
    [15.6, 12.0], [16.5, 12.7], [-13.0, 27.0], [24.0, -3.0], [10.0, -20.0], [-5.0, 24.0],
  ]);

  kit.pallets({
    count: 16,
    area: [-31, 30, -31, 31],
    // encostados na parede do galpão independentemente do sorteio
    extra: [[15.4, 0.02, 3.2, 0.1, 1], [15.4, 0.16, 3.2, 0.35, 1], [15.4, 0.30, 3.25, 0.02, 1]],
  });

  kit.drums([
    [4.2, 4.4], [4.9, 5.1], [4.4, 5.6], [-13.0, -2.0], [-13.7, -2.7], [20.4, 3.0], [21.1, 3.6],
    [-19.0, 14.0], [9.0, -18.0], [9.8, -18.6], [27.5, -4.0], [-29.0, -1.5], [-28.4, -2.3],
    [16.0, -19.5], [-7.0, 18.0], [26.0, 20.0], [-2.0, -25.0], [-1.2, -25.6],
  ]);

  kit.tyres([[-28.0, 5.0, 4], [-26.6, 5.4, 3], [11.5, -11.0, 3], [22.0, 1.5, 4], [-4.5, 22.5, 2], [19.0, -21.5, 3]]);

  // mais pesado onde as paredes estão rompidas
  const hotspots = [[7.5, -33.5, 5], [-33.5, 13.5, 4], [21.2, -20.8, 3.5], [-16.4, -11.2, 3]];
  kit.rubbleField({ count: 210, hotspots });
  kit.bricks({ count: 120, hotspots });
  kit.litter({ count: 130 });
}
